import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CellEvent, ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import { FirstDataRenderedEvent, GridReadyEvent, NewColumnsLoadedEvent } from 'ag-grid-community/dist/lib/events';
import { PayScalesService } from '../../pay-scales.service';
import { ICalculatedPayScaleRates, IPayScale, IYearOfServiceRecords } from '../../pay-scales.types';
import { CurrencyComponent } from './currency.component';
import { NumericEditorComponent } from './numeric.component';
import { TwoDigitDecimalEditorComponent } from './two-digit-decimal.component';
import { EditableHeaderComponent } from './editable-header.component';

/**
 * Handles display of pay yearofservice card
 */
@Component({
	selector: 'app-pay-scales-years-of-service-card',
	templateUrl: './pay-scales-years-of-service-card.component.html'
})
export class PayScalesYearsOfServiceCardComponent implements OnChanges, OnInit {
	constructor(private payScalesService: PayScalesService) {}

	@Input() public isAddRecord: boolean = false;
	@Input() public isApRatingPayScale: boolean = false;
	@Input() public isInstructorPayScale: boolean = false;
	@Input() public payScale: IPayScale;
	@Input() public numberOfTrips: number;
	@Input() public yearOfServiceToHighlight: number = 0;

	@Output() public onChange: EventEmitter<IYearOfServiceRecords[]> = new EventEmitter();

	public api: GridApi;
	public columnApi: ColumnApi;
	public calculatedPayScaleRates: (ICalculatedPayScaleRates & { instructorPayDollars: number })[];
	public payScalesColumnDefs: ColDef[] = [];
	public gridOptions: GridOptions;

	public ngOnInit(): void {
		if (!this.payScale || !this.payScale.yearOfServiceRecords) {
			return;
		}

		this.gridOptions = {
			defaultColDef: {
				resizable: true
			},
			onGridReady: (params: GridReadyEvent): void => {
				params.api.sizeColumnsToFit();
				this.api = params.api;
				this.columnApi = params.columnApi;
			},
			onNewColumnsLoaded: (params: NewColumnsLoadedEvent): void => {
				params.api.sizeColumnsToFit();
			},
			getRowStyle: (params): { [key: string]: string } => {
				if (params.data.yearsOfService === this.yearOfServiceToHighlight) {
					return {
						'box-shadow': '0px 2px 10px 0px rgba(0, 0, 0, 0.5)',
						position: 'relative',
						'margin-bottom': '-20px',
						height: '40px',
						'padding-top': '5px'
					};
				}
			},
			getRowNodeId: (params): string => {
				return params.yearsOfService;
			},
			deltaRowDataMode: true,
			onFirstDataRendered: this.onFirstDataRendered
		};
	}

	public ngOnChanges(): void {
		this.calculatedPayScaleRates = this.payScalesService.calculatePayScaleRates(this.numberOfTrips, this.payScale);

		this.payScalesColumnDefs = [
			{
				headerName: 'Year Of Service',
				field: 'yearsOfService',
				suppressNavigable: true
			},
			{
				headerName: 'Guaranteed Hours',
				editable: this.isAddRecord,
				cellEditorFramework: NumericEditorComponent,
				field: 'guaranteedHours',
				headerComponentFramework: this.isAddRecord ? EditableHeaderComponent : null
			},
			{
				headerName: 'Hourly Rate',
				editable: this.isAddRecord,
				cellEditorFramework: TwoDigitDecimalEditorComponent,
				field: 'hourlyRateDollars',
				headerComponentFramework: this.isAddRecord ? EditableHeaderComponent : null
			},
			{
				headerName: 'Instructor Pay',
				cellEditorFramework: TwoDigitDecimalEditorComponent,
				editable: this.isAddRecord,
				field: 'instructorPayDollars',
				hide: !this.isInstructorPayScale,
				headerComponentFramework: this.isAddRecord ? EditableHeaderComponent : null
			},
			{
				headerName: 'Bi-Weekly',
				field: 'biWeeklyRate',
				cellRendererFramework: CurrencyComponent,
				suppressNavigable: true
			},
			{
				headerName: 'Bi-Weekly A&P',
				field: 'apBiWeeklyRate',
				cellRendererFramework: CurrencyComponent,
				hide: !this.isApRatingPayScale,
				suppressNavigable: true
			},
			{
				headerName: 'Bi-Weekly Instructor',
				field: 'biWeeklyInstructorRate',
				hide: !this.isInstructorPayScale,
				cellRendererFramework: CurrencyComponent,
				suppressNavigable: true
			},
			{
				headerName: 'Monthly',
				field: 'monthlyRate',
				cellRendererFramework: CurrencyComponent,
				suppressNavigable: true
			},
			{
				headerName: 'Annual',
				field: 'annualRate',
				cellRendererFramework: CurrencyComponent,
				suppressNavigable: true
			},
			{
				headerName: 'Annual A&P',
				field: 'apAnnualRate',
				cellRendererFramework: CurrencyComponent,
				hide: !this.isApRatingPayScale,
				suppressNavigable: true
			},
			{
				headerName: 'Trip Rig',
				field: 'tripRigRate',
				cellRendererFramework: CurrencyComponent,
				hide: !this.numberOfTrips,
				suppressNavigable: true
			},
			{
				headerName: 'Trip Rig A&P',
				field: 'apTripRigRate',
				cellRendererFramework: CurrencyComponent,
				hide: !this.isApRatingPayScale || !this.numberOfTrips,
				suppressNavigable: true
			}
		];
	}

	public onFirstDataRendered(params: FirstDataRenderedEvent): void {
		params.api.sizeColumnsToFit();
	}

	public onGridReady(params: GridReadyEvent): void {
		params.api.sizeColumnsToFit();
		this.api = params.api;
		this.columnApi = params.columnApi;
	}

	/**
	 * calculate
	 */
	public calculate(index: number, isNeedAdd: boolean = false): void {
		if (!this.calculatedPayScaleRates || !this.calculatedPayScaleRates.length || isNaN(index)) {
			return;
		}

		// If cells were edited, their values were casted to strings. They must be numbers
		// in order for calculations to work.
		const { guaranteedHours, hourlyRateDollars, instructorPayDollars } = this.calculatedPayScaleRates[index];
		this.calculatedPayScaleRates[index].guaranteedHours = +guaranteedHours;
		this.calculatedPayScaleRates[index].hourlyRateDollars = +hourlyRateDollars;
		this.calculatedPayScaleRates[index].instructorPayDollars = +instructorPayDollars;

		// Since calculated data depends on `payScale` and it isn't the source of the grid's
		// row data, `payScale` must be updated first
		this.calculatedPayScaleRates.forEach((rate, index) => this.updatePayScale(index));

		const total: number = this.calculatedPayScaleRates.length;
		const lastItem: ICalculatedPayScaleRates = this.calculatedPayScaleRates[total - 1];
		if (
			total === index + 1 &&
			isNeedAdd &&
			this.isAddRecord &&
			lastItem.guaranteedHours &&
			lastItem.hourlyRateDollars
		) {
			if (this.isInstructorPayScale && !lastItem.instructorPayDollars) {
				return;
			}
			const newYearOfServiceRecord: IYearOfServiceRecords = {
				yearsOfService: this.calculatedPayScaleRates.length + 1,
				guaranteedHours: 0,
				hourlyRateDollars: 0,
				instructorPayDollars: 0
			};

			this.payScale.yearOfServiceRecords = [...this.payScale.yearOfServiceRecords, newYearOfServiceRecord];
		}

		this.calculatedPayScaleRates = this.payScalesService.calculatePayScaleRates(this.numberOfTrips, this.payScale);

		if (isNeedAdd && lastItem.guaranteedHours && lastItem.hourlyRateDollars) {
			setTimeout(() => {
				this.focusOnNewRow(this.calculatedPayScaleRates.length - 1);
			});
		}

		this.isNeedChange();
	}

	/**
	 *
	 * onCellValueChanged
	 */
	public onCellValueChanged(event: CellEvent): void {
		const totalRows: number = this.calculatedPayScaleRates.length;

		if (totalRows <= this.payScale.yearOfServiceRecords.length) {
			this.payScale.yearOfServiceRecords[event.rowIndex].isNeedUpdate = true;
		}

		const isEditableField: boolean =
			event.colDef.field === 'hourlyRateDollars' || event.colDef.field === 'instructorPayDollars';
		const isNewRecord: boolean = totalRows === event.rowIndex + 1;
		const addRecord: boolean = isEditableField && isNewRecord;
		this.calculate(event.rowIndex, addRecord);
	}

	/**
	 * isNeedChange
	 */
	public isNeedChange(): void {
		this.onChange.emit(this.payScale.yearOfServiceRecords);
	}

	private updatePayScale(index: number): void {
		const {
			guaranteedHours,
			hourlyRateDollars,
			instructorPayDollars,
			yearsOfService
		} = this.calculatedPayScaleRates[index];

		this.payScale.yearOfServiceRecords[index].guaranteedHours = guaranteedHours;
		this.payScale.yearOfServiceRecords[index].hourlyRateDollars = hourlyRateDollars;
		this.payScale.yearOfServiceRecords[index].instructorPayDollars = instructorPayDollars;
		this.payScale.yearOfServiceRecords[index].yearsOfService = yearsOfService;
	}

	private focusOnNewRow(index: number): void {
		const columns: any = this.columnApi.getAllColumns();
		this.api.setFocusedCell(index, columns[1].colId);
		this.api.startEditingCell({
			rowIndex: index,
			colKey: columns[1].colId
		});
	}
}
